/**
 * Authenticated profile chrome for MA UI.
 *
 * Must never render a prior user's name/email, and must never fall back to
 * demo placeholders (e.g. "John Smith" / johndoe88).
 *
 * Storage is scoped to Auth0 sub so User A → logout → User B in the same
 * browser cannot show User A's profile object.
 */

export const USER_INFO_STORAGE_KEY = "userInfo";

/**
 * `userInfo.profileSource` value set when the chrome was synced from the
 * canonical kk-profile SoR (GET/PUT /me/profile) rather than Auth0 claims or
 * a Support Mode handoff snapshot. In-memory only (not persisted).
 */
export const PROFILE_CHROME_SOURCE_KK_PROFILE = "kk-profile";

/**
 * `userInfo.avatarSource` when canonical kk-profile chrome has no avatar and
 * the header/My Profile display the authenticated user's Auth0 picture
 * instead. Display only — never written to kk-profile. In-memory only.
 */
export const AVATAR_SOURCE_AUTH0_FALLBACK = "auth0-fallback";

const EMPTY_PROFILE = Object.freeze({
  firstName: "",
  lastName: "",
  email: "",
  companyAvatar: null,
  avatar: null,
  sub: null,
});

export function emptyProfile() {
  return { ...EMPTY_PROFILE };
}

/**
 * True for a temporary S3 presigned GET URL (kk-profile `avatarUrl`).
 * These are derived display credentials with a TTL — never profile identity.
 */
export function isPresignedUrl(value) {
  if (typeof value !== "string") return false;
  return /[?&]X-Amz-(Signature|Algorithm|Credential)=/i.test(value);
}

/**
 * Avatar value that may be written to localStorage: only a stable http(s)
 * image URL (Auth0 picture / legacy kk-profile http avatar). Presigned URLs
 * and raw S3 keys are never persisted — canonical hydration re-derives them.
 */
export function persistableAvatar(value) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  if (isPresignedUrl(trimmed)) return null;
  return trimmed;
}

/**
 * Avatar display precedence for canonical kk-profile chrome:
 *   1. canonical kk-profile avatar (presigned avatarUrl / legacy stored http)
 *   2. the authenticated user's own Auth0 picture — direct login only
 *   3. null → initials
 * Support Mode never falls back: `auth_user` may be the SA operator, not the
 * selected user.
 * @returns {{ avatar: string|null, avatarSource: string|null }}
 */
export function resolveCanonicalChromeAvatar(chrome, authUser) {
  if (chrome?.avatar) {
    return { avatar: chrome.avatar, avatarSource: PROFILE_CHROME_SOURCE_KK_PROFILE };
  }
  const sameSub =
    Boolean(authUser?.sub) &&
    Boolean(chrome?.sub) &&
    String(authUser.sub) === String(chrome.sub);
  const picture =
    chrome?.supportMode !== true && sameSub
      ? persistableAvatar(authUser.picture)
      : null;
  return picture
    ? { avatar: picture, avatarSource: AVATAR_SOURCE_AUTH0_FALLBACK }
    : { avatar: null, avatarSource: null };
}

/**
 * True when `profile` is in-memory canonical kk-profile chrome for a DIRECT
 * login session (not Support Mode) belonging to `authSub`.
 */
export function isCanonicalProfileChromeFor(profile, authSub) {
  if (!profile || typeof profile !== "object" || !authSub) return false;
  return (
    profile.profileSource === PROFILE_CHROME_SOURCE_KK_PROFILE &&
    profile.supportMode !== true &&
    String(profile.sub || "") === String(authSub)
  );
}

/**
 * Map Auth0 user (from getUser / /userinfo / auth_user) to display profile.
 */
export function mapAuth0UserToProfile(authUser) {
  if (!authUser || typeof authUser !== "object") {
    return emptyProfile();
  }

  const given =
    String(authUser.given_name || "").trim() ||
    String(authUser.name || "")
      .trim()
      .split(/\s+/)[0] ||
    "";
  const family =
    String(authUser.family_name || "").trim() ||
    (() => {
      const parts = String(authUser.name || "")
        .trim()
        .split(/\s+/);
      return parts.length > 1 ? parts.slice(1).join(" ") : "";
    })();

  return {
    firstName: given,
    lastName: family,
    email: String(authUser.email || "").trim(),
    companyAvatar: null,
    avatar: authUser.picture || null,
    sub: authUser.sub ? String(authUser.sub) : null,
  };
}

/**
 * Display fields for header/dropdown. Never invent demo identity.
 */
export function resolveAuthenticatedDisplayIdentity(userInfo) {
  const firstName = String(userInfo?.firstName || "").trim();
  const lastName = String(userInfo?.lastName || "").trim();
  const email = String(userInfo?.email || "").trim();
  const fullName = [firstName, lastName].filter(Boolean).join(" ");
  return {
    firstName,
    lastName,
    email,
    fullName,
    greetingName: firstName || "there",
    headerLabel: fullName || email || "Account",
    initials: resolveUserInitials({ firstName, lastName, email, fullName }),
  };
}

/**
 * Initials for avatar fallback. Never invent demo identity.
 * Returns "" when no usable identity is present — callers must distinguish
 * "still loading" (neutral placeholder) from "resolved but incomplete" (??).
 */
export function resolveUserInitials({
  firstName = "",
  lastName = "",
  email = "",
  fullName = "",
} = {}) {
  const first = String(firstName || "").trim();
  const last = String(lastName || "").trim();
  if (first && last) {
    return `${first[0]}${last[0]}`.toUpperCase();
  }
  if (first) {
    return first.slice(0, 2).toUpperCase();
  }
  const name = String(fullName || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  }
  const mail = String(email || "").trim();
  if (mail) return mail.slice(0, 2).toUpperCase();
  return "";
}

/**
 * True when name/email fields can produce non-empty initials.
 * Empty fields while a profile fetch is in flight are NOT "ready".
 */
export function hasUsableAvatarIdentity({
  firstName = "",
  lastName = "",
  email = "",
  fullName = "",
} = {}) {
  return Boolean(
    resolveUserInitials({ firstName, lastName, email, fullName }),
  );
}

/**
 * Post-resolution fallback when identity loaded but still has no usable name.
 * Kept as "??" for deterministic incomplete-profile display only.
 */
export function unresolvedAvatarInitialsFallback() {
  return "??";
}

export function clearStoredUserInfo() {
  try {
    localStorage.removeItem(USER_INFO_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Persist profile scoped to Auth0 sub. Legacy unscoped blobs are rewritten.
 * A presigned kk-profile `avatarUrl` is NEVER persisted (see persistableAvatar).
 */
export function persistUserInfo(profile, authSub) {
  if (!profile) {
    clearStoredUserInfo();
    return;
  }
  try {
    const sub = authSub || profile.sub || null;
    if (sub) {
      localStorage.setItem(
        USER_INFO_STORAGE_KEY,
        JSON.stringify({
          sub: String(sub),
          profile: {
            firstName: profile.firstName || "",
            lastName: profile.lastName || "",
            email: profile.email || "",
            companyAvatar: profile.companyAvatar || null,
            avatar: persistableAvatar(profile.avatar),
            sub: String(sub),
          },
        }),
      );
    } else {
      // Unscoped write is unsafe across users — do not persist.
      clearStoredUserInfo();
    }
  } catch {
    /* ignore quota / private mode */
  }
}

/**
 * Load stored profile only when it matches the current Auth0 sub.
 * Discards legacy unscoped userInfo and cross-user leftovers.
 */
export function loadStoredUserInfo(authSub) {
  try {
    const raw = localStorage.getItem(USER_INFO_STORAGE_KEY);
    if (!raw) return emptyProfile();

    const parsed = JSON.parse(raw);

    // New scoped shape: { sub, profile }
    if (parsed && typeof parsed === "object" && parsed.profile) {
      if (!authSub || String(parsed.sub) !== String(authSub)) {
        clearStoredUserInfo();
        return emptyProfile();
      }
      return {
        ...emptyProfile(),
        ...parsed.profile,
        sub: String(authSub),
      };
    }

    // Legacy unscoped profile blob — only keep if it already carries matching sub
    if (parsed && typeof parsed === "object") {
      if (authSub && parsed.sub && String(parsed.sub) === String(authSub)) {
        return { ...emptyProfile(), ...parsed, sub: String(authSub) };
      }
      clearStoredUserInfo();
      return emptyProfile();
    }

    clearStoredUserInfo();
    return emptyProfile();
  } catch {
    clearStoredUserInfo();
    return emptyProfile();
  }
}

/**
 * Prefer Auth0-derived identity; merge optional local avatar overrides
 * only when they belong to the same sub.
 */
export function syncProfileForAuthenticatedUser(authUser, storedProfile) {
  const fromAuth = mapAuth0UserToProfile(authUser);
  if (!fromAuth.sub) {
    return emptyProfile();
  }
  const stored =
    storedProfile &&
    storedProfile.sub &&
    String(storedProfile.sub) === String(fromAuth.sub)
      ? storedProfile
      : null;

  return {
    ...fromAuth,
    // Keep local avatar/companyAvatar edits for the same authenticated user.
    companyAvatar: stored?.companyAvatar || fromAuth.companyAvatar,
    avatar: stored?.avatar || fromAuth.avatar,
  };
}
